export const MARKDOWN_RULES_PROMPT = `请只对上面的最新版文章添加 WeDraft 支持的 Markdown 排版标记。

这是一个“只排版，不改稿”任务。

【最高优先级：原文不变】

1. 原文的标题、正文、事实、数据、观点、措辞、语气、标点、段落顺序和信息量必须保持不变。
2. 不得改写、润色、缩写、扩写、总结、重组、删除、补充或调整原文。
3. 不得凭空增加标题、小标题、过渡句、结论、案例、数据、列表、表格、引用或参考来源。
4. 只允许添加 Markdown 符号和排版所需的空行；不能改变原文可见文字。
5. 如果原文的某种结构不明确，保持原样，不要自行推测或创造。

【最终输出方式】

最终只能输出一个 Markdown 代码块：

~~~markdown
这里放完整文章
~~~

代码块外不能出现解释、提示、总结或“已完成”等文字。

必须输出原始 Markdown 字符，不要把 Markdown 渲染成普通富文本。

我会点击代码块右上角的复制按钮，将代码块内容复制到 WeDraft。

【文章基本格式】

- 第一行保留原文标题，标题前不添加“#”。
- 标题下面空一行，再写正文。
- 保留原文段落顺序；段落、标题、列表、引用、代码、图片和表格之间各空一行。
- 不要主动添加感谢语、“- End -”、封面海报来源或联系信息；如果原文已有，则原样保留。

【章节】

只对原文已经明确存在的章节标题添加标记，不得自行概括或新建章节标题。

原文已有的一级章节使用连续编号，并在前面添加分割线：

---

## 1. 原有一级章节标题

一级章节格式：

## 1. 章节标题
## 2. 章节标题
## 3. 章节标题

原文已有的二级章节不编号：

### 二级章节标题

【重点、数字与金句】

1. 关键数字、关键词、重要信息和金句，默认只使用粗体：

**重点内容**

2. 关键数字要连同必要单位一起加粗，例如：**128亿元**、**增长18%**、**3年**。
3. 粗体标记必须直接包住内容，标记内不留空格；与前后中文正文可以直接相连。例如：市占率**42.5%**提升。不要为了加粗而改变原文空格。
4. 粗体要克制，只加粗真正关键的词、数字或句子，不要整段加粗。
5. 普通金句只加粗，保留在原段落和原位置，不要拆出来独立成行。
6. 只有同时满足以下条件的一句话，才能独立成行并使用“引用 + 粗体”：
   - 原文本身就是一句完整、可独立理解的核心观点；
   - 它是全文极少数、特别重要的结论；
   - 将它独立展示不会改变原意或破坏上下文。

格式：

> **这是一句特别重要、需要独立展示的核心观点。**

7. 全文这种独立金句宁少勿多；如果不确定，只加粗，不使用引用样式。
8. 原文本身已明确是普通引用的内容，才使用：

> 原文引用内容

“>”后必须保留一个空格。不要把普通段落转换成引用。

【表格：默认禁止新建】

1. 不得因为原文出现了数字、金额、百分比、时间、参数、分类或多个对象，就自行创建表格。
2. 不得从普通段落、列表或分散叙述中提取内容、改写表头或重组数据来制作表格。
3. 只有原文本身已经是明确的表格、行列矩阵，或用户明确要求“把这一段转成表格”时，才允许输出 Markdown 表格。
4. 如果不确定是否应该使用表格，必须保留原文形式，不使用表格。

原文确实已是表格时，使用标准 GFM Markdown 管道语法：

| 指标 | 当前值 | 变化 |
| :--- | ---: | :---: |
| 用户数 | 12,800 | **+18%** |
| 转化率 | 6.4% | -0.7% |

表格必须满足：

- 第一行是表头，第二行是对齐分隔行，第三行开始是数据。
- 每一行都使用英文半角竖线“|”，列数必须完全一致，表格内部不能有空行。
- 使用 2 至 4 列，表头和单元格保持简短。
- 不使用合并单元格，不输出 HTML 表格。
- 单元格内的竖线写成“\\|”。
- 重要数据可以加粗，但不能将整张表格加粗。
- \`:---\` 左对齐，适合文字；\`---:\` 右对齐，适合数字；\`:---:\` 居中，适合状态。

【列表】

只有原文已经明确是并列项、步骤或顺序项时，才使用列表；不得把普通段落擅自拆成列表。

无序列表：

- 第一项
- 第二项

有序列表：

1. 第一步
2. 第二步

【代码】

原文的行内代码使用单个反引号：\`const value = 1\`

原文的多行代码使用三个反引号，并标注语言。必须保留代码的原有换行和缩进。

【图片】

只保留原文已有图片，不得凭空添加图片、图片说明或图片来源。

格式：

![原图片说明](图片地址 "图片来源：原来源")

【补充说明和参考来源】

- 只对原文已有的“说明：”“数据来源：”“作者：”“编辑：”添加排版，不得新增。
- 只有原文已经提供参考来源时，才在全文最后使用“## 参考来源”和 1 至 10 条有序列表。
- 不得搜索、猜测或编造参考来源。
- “参考来源”必须是最后一个章节。

【输出前必须检查】

- 逐句对比原文：除 Markdown 符号和空行外，可见文字是否完全一致。
- 是否没有改写、删除、新增、总结或重组任何内容。
- 是否没有凭空创建表格、列表、标题、引用或参考来源。
- 关键数字、关键词、重要信息和金句是否已克制地加粗。
- 普通金句是否仅加粗并保留原位置。
- 是否只有极少数特别重要的单句观点使用了“> **金句**”。
- 如果原文不是表格，是否完全没有使用表格。
- 是否只输出一个 Markdown 代码块，代码块外没有任何文字。

上面的最新版，直接按照以上规则输出最终文章。`;
